/**
 * Browser entry point. Everything interesting lives in `App.tsx` and
 * `router.tsx` so a test can mount the identical tree without a DOM element or
 * a browser history.
 */
import "./index.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { APP_VERSION } from "./appVersion";
import { createAppQueryClient } from "./queryClient";
import { createAppRouter } from "./router";
import {
  isSupportWidgetHost,
  setSupportWidgetLocale,
  startSupportWidget,
  trackSupportWidgetRoute,
} from "./supportWidget";

const container = document.getElementById("root");
if (!container) {
  throw new Error(
    'index.html is missing <div id="root">. Nothing can mount without it.',
  );
}

// Before the first render, so the support chat's listeners and the phone
// width button hide are queued ahead of anything SupportWidgetIdentity queues
// from an effect.
startSupportWidget(window.location.hostname, document, window, (query) =>
  window.matchMedia(query),
);

const queryClient = createAppQueryClient();
const router = createAppRouter({ queryClient });

// The chat's language and the screen the fan is on, production host only.
if (isSupportWidgetHost(window.location.hostname)) {
  setSupportWidgetLocale(window, document.documentElement.lang);
  trackSupportWidgetRoute({
    win: window,
    appVersion: APP_VERSION,
    currentRoute: () => router.state.matches.at(-1)?.fullPath ?? "/",
    subscribe: (listener) => router.subscribe("onResolved", listener),
  });
}

createRoot(container).render(
  <StrictMode>
    <App queryClient={queryClient} router={router} />
  </StrictMode>,
);
